/**
 * 用户端时间：接口给瞬间（毫秒时间戳，或历史纽约墙钟字符串），展示用设备时区。
 * 开盘 / 日切在服务端按 America/New_York，这里只负责解析和展示。
 */

export const BUSINESS_TIME_ZONE = 'America/New_York'

function pad(value) {
  return String(value).padStart(2, '0')
}

function partNumber(parts, type) {
  const found = parts.find((part) => part.type === type)
  let value = found ? Number(found.value) : 0
  if (type === 'hour' && value === 24) {
    value = 0
  }
  return value
}

/**
 * 把无偏移日期时间当成指定 IANA 时区的墙钟，换成 UTC 毫秒。
 */
export function parseNaiveInTimeZone(naiveStr, timeZone) {
  if (!naiveStr) {
    return null
  }
  const clean = String(naiveStr).trim().replace('T', ' ')
  const matched = clean.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/)
  if (!matched) {
    return null
  }
  const year = Number(matched[1])
  const month = Number(matched[2])
  const day = Number(matched[3])
  const hour = Number(matched[4] || 0)
  const minute = Number(matched[5] || 0)
  const second = Number(matched[6] || 0)

  let utc = Date.UTC(year, month - 1, day, hour, minute, second)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(new Date(utc))

  const actual = Date.UTC(
    partNumber(parts, 'year'),
    partNumber(parts, 'month') - 1,
    partNumber(parts, 'day'),
    partNumber(parts, 'hour'),
    partNumber(parts, 'minute'),
    partNumber(parts, 'second')
  )
  return utc + (Date.UTC(year, month - 1, day, hour, minute, second) - actual)
}

export function toTimestamp(value) {
  if (value == null || value === '') {
    return null
  }
  if (value instanceof Date) {
    const time = value.getTime()
    return Number.isFinite(time) ? time : null
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) {
      return null
    }
    return value < 1e12 ? value * 1000 : value
  }
  const text = String(value).trim()
  if (!text || text === '--' || text === '-') {
    return null
  }
  if (/^-?\d+$/.test(text)) {
    return toTimestamp(Number(text))
  }
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(text)) {
    const parsed = Date.parse(text)
    return Number.isFinite(parsed) ? parsed : null
  }
  const naive = parseNaiveInTimeZone(text, BUSINESS_TIME_ZONE)
  if (naive != null) {
    return naive
  }
  const fallback = Date.parse(text)
  return Number.isFinite(fallback) ? fallback : null
}

export function formatDateTime(value, timeZone) {
  const timestamp = toTimestamp(value)
  if (timestamp == null) {
    return '--'
  }
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }
  if (timeZone) {
    options.timeZone = timeZone
  }
  const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(new Date(timestamp))
  const year = parts.find((part) => part.type === 'year').value
  const month = parts.find((part) => part.type === 'month').value
  const day = parts.find((part) => part.type === 'day').value
  const hour = parts.find((part) => part.type === 'hour').value
  const minute = parts.find((part) => part.type === 'minute').value
  const second = parts.find((part) => part.type === 'second').value
  return `${year}/${month}/${day} ${hour}:${minute}:${second}`
}

export function formatDateTimeDash(value, timeZone) {
  return formatDateTime(value, timeZone).replace(/\//g, '-')
}

/** K 线轴/十字光标：设备时区，欧美日期时间 MM/DD/YYYY HH:mm:ss */
export function formatKlineDateTime(value) {
  const timestamp = toTimestamp(value)
  if (timestamp == null) {
    return '--'
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(new Date(timestamp))
  const year = parts.find((part) => part.type === 'year').value
  const month = parts.find((part) => part.type === 'month').value
  const day = parts.find((part) => part.type === 'day').value
  let hour = parts.find((part) => part.type === 'hour').value
  if (hour === '24') {
    hour = '00'
  }
  const minute = parts.find((part) => part.type === 'minute').value
  const second = parts.find((part) => part.type === 'second').value
  return `${month}/${day}/${year} ${hour}:${minute}:${second}`
}

export function remainingSeconds(start, durationSeconds, currentTimestamp) {
  const startTs = toTimestamp(start)
  const duration = Math.max(0, Math.floor(Number(durationSeconds) || 0))
  if (startTs == null) {
    return duration
  }
  const now = currentTimestamp == null ? Date.now() : currentTimestamp
  const remain = Math.floor((startTs + duration * 1000 - now) / 1000)
  return Math.max(0, Math.min(duration, remain))
}

export function addSeconds(value, seconds) {
  const timestamp = toTimestamp(value)
  if (timestamp == null) {
    return null
  }
  return timestamp + (Number(seconds) || 0) * 1000
}

export function parseNYTimeToTimestamp(nyTimeStr) {
  return toTimestamp(nyTimeStr)
}

export function formatTimestampToLocal(utcTimestamp) {
  return formatDateTime(utcTimestamp)
}

export function convertNYTimeToLocal(nyTimeStr) {
  return formatDateTime(nyTimeStr)
}

export function calculateCountdownFromNYTime(nyStartTime, durationSeconds, currentTimestamp) {
  return remainingSeconds(nyStartTime, durationSeconds, currentTimestamp)
}

export function getCurrentNYTime() {
  return formatDateTimeDash(Date.now(), BUSINESS_TIME_ZONE)
}

export function isDaylightSavingTime() {
  return false
}

export default {
  BUSINESS_TIME_ZONE,
  toTimestamp,
  parseNaiveInTimeZone,
  formatDateTime,
  formatDateTimeDash,
  formatKlineDateTime,
  remainingSeconds,
  addSeconds,
  parseNYTimeToTimestamp,
  formatTimestampToLocal,
  convertNYTimeToLocal,
  calculateCountdownFromNYTime,
  getCurrentNYTime,
  isDaylightSavingTime
}
